import { create } from 'zustand';
import { request } from '../utils';
import { Order } from '../components/Transactions/Order';
import { ORDER_STATUS } from '../utils/constant';

interface OrdersState {
  orders: Order[];
  recentOrders: Order[];
  filteredOrders: Order[];
  loading: boolean;
  error: string | null;
  hasInitialLoad: boolean;
  loadOrders: () => Promise<void>;
  updateOrder: (orderId: string, updates: Partial<Order>) => void;
  setFilteredOrders: (orders: Order[]) => void;
  refreshRecentOrders: () => Promise<void>;
  addOrder: (order: Order) => void;
}

// 2. Create the store
export const useOrdersStore = create<OrdersState>((set, get) => ({
  orders: [],
  recentOrders: [],
  filteredOrders: [],
  loading: true,
  error: null,
  hasInitialLoad: false,

loadOrders: async () => {
  set({ loading: true });
  try {
    const response = await request.get({
      url: "/order/my-orders",
    });
    const ordersData = Array.isArray(response.data)
      ? (response.data as Order[])
      : [];

    const recentOrders = ordersData
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);

    set({
      orders: ordersData,
      recentOrders: recentOrders,
      filteredOrders: ordersData,
      hasInitialLoad: true,
      loading: false,
    });
  } catch (error) {
    set({ error: "Failed to load order data", loading: false });
    console.error("Failed to load orders:", error);
  }
},

updateOrder: (orderId: string, updates: Partial<Order>) => {
  const { orders, filteredOrders } = get();

  const updatedOrders = orders.map((order) =>
    order._id === orderId ? { ...order, ...updates } : order
  );

  const updatedFilteredOrders = filteredOrders.map((order) =>
    order._id === orderId ? { ...order, ...updates } : order
  );

  const recentOrders = [...updatedOrders]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  set({
    orders: updatedOrders,
    filteredOrders: updatedFilteredOrders,
    recentOrders: recentOrders,
  });
},

  refreshRecentOrders: async () => {
    const { orders } = get();

    const processingOrders = orders.filter(
      (order) => order.status === ORDER_STATUS.PROCESSING
    );

    if (processingOrders.length === 0) return;

    try {
      // ---- Process in batches to avoid overwhelming the server
      const BATCH_SIZE = 3;
      let updatedCount = 0;

      for (let i = 0; i < processingOrders.length; i += BATCH_SIZE) {
        const batch = processingOrders.slice(i, i + BATCH_SIZE);

        const batchPromises = batch.map(async (order, batchIndex) => {
          const globalIndex = i + batchIndex;

          try {
            const response = await request.get({
              url: `/order/${order._id}`,
            });

            const updatedOrder = response.data as Order;

            // ---- Update if status or payment status changed
            if (
              updatedOrder.status !== order.status ||
              updatedOrder.buyerMadePayment !== order.buyerMadePayment
            ) {
              const { updateOrder } = get();
              updateOrder(order._id, updatedOrder);
              return true;
            }

            return false;
          } catch (error) {
            console.error(
              `❌ [${globalIndex}] Failed to refresh order ${order._id}:`,
              error
            );
            return false;
          }
        });

        const batchResults = await Promise.all(batchPromises);
        updatedCount += batchResults.filter(Boolean).length;

        // ---- Small delay between batches to be nice to the server
        if (i + BATCH_SIZE < processingOrders.length) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }

      if (updatedCount > 0) {
        return;
      }
    } catch (error) {
      console.error("💥 Failed to refresh recent orders:", error);
    }
  },

  setFilteredOrders: (filteredOrders: Order[]) => {
    set({ filteredOrders });
  },

addOrder: (order: Order) => {
  const { orders, filteredOrders } = get();

  const newOrders = [order, ...orders];
  const newFilteredOrders = [order, ...filteredOrders];
  
  const newRecentOrders = [...newOrders]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  set({
    orders: newOrders,
    filteredOrders: newFilteredOrders,
    recentOrders: newRecentOrders,
  });
},
}));